import * as React from 'react';
import {
    AnyObject,
    IComponentViewController,
    IViewManager,
    root
} from '@estee/elc-universal-utils';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { ErrorBoundary } from '@estee/elc-telemetry';
import { ViewDataPreloader, IConfigsCollection } from '../preloader/ViewDataPreloader';
import { LazyRenderer } from './LazyRenderer';

export type ComponentNodeType = {
    name: string;
    config: string | null;
    component: React.ReactElement | null;
    productData: string | null;
    node: HTMLElement;
    /* Why the component could not be resolved. Set by getComponentForNode. */
    failureReason?: string;
};

interface IComponentPropsFunctions {
    [key: string]: Function | object | null;
}

interface IComponentProps extends IComponentPropsFunctions {
    config: object;
    viewController: IComponentViewController | null;
    parentMountPointNode: Element;
}

const { name: serviceName, version } = __serviceInfo__;

export abstract class ComponentRenderer extends LazyRenderer {
    private preloader: ViewDataPreloader | null = null;

    protected readonly logger: ELCLogger = new ELCLogger({
        serviceName,
        serviceVersion: version,
        environment: root.env,
        buid: root.buid
    });

    protected safeJSONParse<T>(data: string | null | undefined, defaultValue: T) {
        try {
            return data ? JSON.parse(data) : defaultValue;
        } catch (error) {
            this.logger.error({
                triggerType: TriggerType.render,
                message: `Failed parsing object: (${data})`,
                payload: {
                    error
                }
            });

            return defaultValue;
        }
    }

    private handleEventsFromProps = (node: HTMLElement, parsedConfig: AnyObject) =>
        Object.keys(parsedConfig)
            .filter((configKey) => parsedConfig[configKey] === '__event__')
            .reduce((eventHandlers, configKey) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const eventHandler = function (...args: any[]) {
                    const event = new CustomEvent(configKey, {
                        detail: args,
                        cancelable: true,
                        bubbles: false
                    });
                    node.dispatchEvent(event);
                };

                return { ...eventHandlers, [configKey]: eventHandler };
            }, {});

    private getConfig = async (component: ComponentNodeType) => {
        let configs: IConfigsCollection | null = null;
        // Don't overload components for non exported views otherwise will crash
        if (this.getNodeAttribute(component.node, 'data-version')) {
            if (this.preloader) {
                await this.preloader.preload();
                configs = this.preloader.getPreloadedConfigs();
            }
        }

        return { configs };
    };

    private getElementProps = async (
        component: ComponentNodeType,
        viewManager: IViewManager
    ): Promise<IComponentProps> => {
        const parsedConfig = this.safeJSONParse<{}>(component.config, {});
        const dataRenderMethod = component.node.getAttribute(this.renderMethodAttribute);

        const parsedProductData = this.safeJSONParse<[]>(component.productData, []);
        const viewController = viewManager.controllerFromView;

        const { configs } = await this.getConfig(component);
        const fnProps = this.handleEventsFromProps(component.node, parsedConfig);

        return {
            ...fnProps,
            config: {
                ...configs,
                ...parsedConfig,
                renderMethod: dataRenderMethod
            },
            productData: parsedProductData,
            parentMountPointNode: component.node.parentNode as Element,
            viewController
        };
    };

    private getElementToRender = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        registeredComponent: React.ComponentType<any>,
        props: IComponentProps,
        viewName: string
    ) => React.createElement(ErrorBoundary, {
        serviceName,
        name: viewName,
        children: React.createElement(registeredComponent, props)
    });

    /* IViewManager does not expose this, but ViewManager leaves `service` unset
       when no registered service claims the view, and loadComponent is wrapped
       in `if (this.service)`. Read the manager rather than the registry:
       getViewManager caches, so a manager built before its service registered
       would look resolved while still no-opping. */
    private hasResolvedService = (viewManager: IViewManager) =>
        (viewManager as IViewManager & { service?: unknown }).service !== undefined;

    protected getComponentForNode = async (node: ComponentNodeType) => {
        const nodeObject = { ...node };

        const viewManager = root.ViewsManager.getViewManager(node.name);

        await viewManager.loadComponent();
        const module = viewManager.module;
        if (module) {
            const props = await this.getElementProps(nodeObject, viewManager);
            nodeObject.component = this.getElementToRender(module, props, nodeObject.name);
        } else {
            /* Returned, not logged: this runs once per retry pass, so logging
               here reports a transient miss as an error. The caller reports
               once, at the cap. Wording rationale in BEXONE-424. */
            nodeObject.failureReason = this.hasResolvedService(viewManager)
                ? `Module failed to load for view: (${node.name})`
                : `View not registered by any service: (${node.name})`;
        }

        return Promise.resolve(nodeObject);
    };

    protected convertNodeToComponentNodeType = (node: HTMLElement) => ({
        component: null,
        config: this.getNodeAttribute(node, 'data-config'),
        productData: this.getNodeAttribute(node, 'product-data'),
        name: this.getNodeAttribute(node, 'data-component') || '',
        node
    });

    public setDataPreloader = (preloader: ViewDataPreloader) => {
        this.preloader = preloader;
    };

    public getDataPreloader = () => this.preloader;
}
